import type { UseFetchOptions, UseFetchReturn } from "@vueuse/core";
import { computed, type MaybeRef, toValue } from "vue";

import type { ApiDesk, NewApiDesk } from "@/types/desk.d.ts";
import { desksApiClient } from "@/utils/apiClients.ts";

function useSavedDesks(
  options: UseFetchOptions = {},
): UseFetchReturn<ApiDesk[]> {
  return desksApiClient("desks", {
    immediate: false,
    refetch: false,
    ...options,
  }).json();
}

function useCreateDesks(
  newDesk: MaybeRef<NewApiDesk | undefined>,
  options: UseFetchOptions = {
    immediate: false,
    refetch: false,
  },
): UseFetchReturn<ApiDesk> {
  return desksApiClient("desks", {
    ...options,
    async beforeFetch() {
      if (!toValue(newDesk)) {
        throw new Error("no valid desk to create");
      }
    },
  })
    .post(() => toValue(newDesk))
    .json();
}

function useUpdateDesk(
  updatedDesk: MaybeRef<ApiDesk | undefined>,
  options?: UseFetchOptions,
): UseFetchReturn<ApiDesk> {
  const transformedDesk = computed(() => {
    const desk = toValue(updatedDesk);
    if (desk) {
      return desk;
    }
    return [];
  });

  return desksApiClient("desks", {
    immediate: false,
    refetch: false,
    beforeFetch() {
      if (!transformedDesk.value) {
        throw new Error("No valid desk provided");
      }
    },
    ...options,
  })
    .post(() => toValue(transformedDesk))
    .json();
}

function useSwitchDesk(
  deskId: MaybeRef<string | undefined>,
  options?: UseFetchOptions,
): UseFetchReturn<ApiDesk[]> {
  const url = computed(() => `desks/${toValue(deskId)}/select`);
  return desksApiClient(url, {
    immediate: false,
    refetch: false,
    beforeFetch() {
      if (!deskId) {
        throw new Error("No valid desk provided");
      }
    },
    ...options,
  })
    .post()
    .json();
}

function useDeleteDesk(
  deskId: MaybeRef<string | undefined>,
  options?: UseFetchOptions,
): UseFetchReturn<unknown> {
  const url = computed(() => `desks/${toValue(deskId)}`);
  return desksApiClient(url, {
    immediate: false,
    refetch: false,
    ...options,
  }).delete();
}

function useJoinDesks(
  deskId: MaybeRef<string | null>,
  shareToken: MaybeRef<string | null>,
  options: UseFetchOptions = {
    immediate: false,
    refetch: false,
  },
): UseFetchReturn<ApiDesk> {
  const url = computed(() => `desks/${toValue(deskId)}/join`);
  return desksApiClient(url, {
    immediate: false,
    refetch: false,
    async beforeFetch() {
      if (toValue(deskId) === null || toValue(shareToken) === null) {
        throw new Error(`Can't join desk, no valid Id or shareToken`);
      }
    },
    ...options,
  })
    .post(() => ({
      shareToken: toValue(shareToken),
    }))
    .json();
}

function useShareDesk(
  deskId: MaybeRef<string>,
  options?: UseFetchOptions,
): UseFetchReturn<{
  deskId: string;
  shareToken: string;
}> {
  const url = computed(() => `desks/${toValue(deskId)}/share`);
  return desksApiClient(url, {
    immediate: false,
    refetch: false,
    ...options,
  })
    .post()
    .json();
}

function useUnshareDesk(
  deskId: MaybeRef<string>,
  options?: UseFetchOptions,
): UseFetchReturn<{
  deskId: string;
  shareToken: string;
}> {
  const url = computed(() => `desks/${toValue(deskId)}/unshare`);
  return desksApiClient(url, { immediate: false, refetch: false, ...options })
    .post()
    .json();
}

export {
  useCreateDesks,
  useDeleteDesk,
  useJoinDesks,
  useSavedDesks,
  useShareDesk,
  useSwitchDesk,
  useUnshareDesk,
  useUpdateDesk,
};
